/**
 * Operational Verification metinleri (K5; ADR 0089): gemide planlanan operasyonun risk
 * doğrulaması, yetki kararı ve operasyonun yaşam döngüsü. Adlar ortağın belgelerindeki gibi
 * İngilizce (FD §9, §11; SS §5–§8); Türkçe ikinci dil.
 */

export const riskVerificationEn = {
  title: 'Operational Verification',
  description:
    'Verify a planned operation against the current company risk assessment before work starts: six common checks, activity-specific checks and mandatory controls, then authorisation by the master or an authorised officer.',
  shipNote:
    'Six “Yes” answers are not permission to start: the verification goes to Pending Authorization. A failed check puts it On Hold; a new uncontrolled hazard escalates it. Anyone on board can stop the work at any time.',
  officeNote:
    'Verifications are made on board and arrive with synchronisation; the office can only view them.',
  vessel: 'Vessel',
  stats: {
    open: 'Being Verified',
    pending: 'Pending Authorization',
    blocked: 'On Hold / Stopped',
    active: 'Authorized / In Progress',
  },
  search: 'Search by number, operation, location or risk assessment',
  filter: {
    state: 'Status',
    states: {
      all: 'All',
      open: 'Being verified',
      pending_authorization: 'Pending authorization',
      blocked: 'On hold, escalated, suspended or reverification',
      active: 'Authorized or in progress',
      closed: 'Completed, closed or cancelled',
    },
  },
  new: 'New Verification',
  columns: {
    number: 'No.',
    operation: 'Planned Operation',
    assessment: 'Risk Assessment',
    planned: 'Planned (Ship Time)',
    checks: 'Checks',
    residual: 'Residual Risk',
    status: 'Status',
    authorization: 'Authorization',
  },
  open: 'Open verification {{number}}',
  none: '—',
  checksValue: '{{answered}} / {{total}}',
  failedCount: '{{count}} No',
  validUntil: 'Valid until {{time}}',
  until: 'until {{time}}',
  showing: 'Showing {{from}}–{{to}} of {{total}}',
  pagination: 'Verification pages',
  tableLabel: 'Operational verifications',
  empty: {
    title: 'No verifications yet',
    ship: 'Start a verification before an operation such as mooring, anchoring, bunkering or enclosed space entry.',
    office: 'This vessel has not sent any verification yet.',
    noMatch: 'No verification matches the filters.',
    error: 'Verifications could not be loaded',
    noVessel: 'No vessel is visible to you.',
    noCandidates:
      'No company risk assessment is assigned to this vessel yet. The office publishes and assigns them.',
  },
  statuses: {
    draft: 'Draft',
    incomplete: 'Incomplete',
    verification_completed: 'Verification Completed',
    pending_authorization: 'Pending Authorization',
    on_hold: 'On Hold',
    escalated: 'Escalated',
    authorized: 'Authorized',
    in_progress: 'In Progress',
    suspended: 'Suspended',
    completed: 'Completed',
    closed: 'Closed',
    cancelled: 'Cancelled',
    reverification_required: 'Reverification Required',
  },
  events: {
    submitted: 'Submitted',
    returned: 'Returned for correction',
    rejected: 'Rejected',
    authorized: 'Authorized',
    started: 'Operation started',
    conditions_changed: 'Conditions changed',
    stopped: 'Work stopped',
    reverification_started: 'Reverification started',
    completed: 'Operation completed',
    closed: 'Closed',
    cancelled: 'Cancelled',
    briefed: 'Briefing acknowledged',
  },
  create: {
    title: 'New Operational Verification',
    version: 'Risk Assessment (current version)',
    versionPlaceholder: 'Pick the assessment for this operation',
    blocked: 'Critical residual risk: the work cannot be authorised.',
    masterRequired: 'High residual risk: only the master can authorise.',
    items:
      '{{count}} checks: 6 common, {{activity}} activity-specific, {{controls}} mandatory controls',
    operation: 'Planned Operation',
    operationPlaceholder: 'e.g. Berthing at Aliağa, port side to',
    location: 'Location',
    plannedStart: 'Planned Start (Ship Time)',
    plannedEnd: 'Planned End (Ship Time)',
    timesNote: 'Times are entered in ship time ({{offset}}) and stored in UTC.',
    confirm: 'Create Verification',
  },
  edit: {
    title: 'Edit Planned Operation',
    confirm: 'Save',
  },
  detail: {
    back: 'All verifications',
    findingsCard: 'Findings',
    noFindings: 'No findings on this verification.',
    reportFinding: 'Report Finding',
    findingsBlocking:
      'An open blocking finding holds the authorisation: correct, reverify and close it first.',
    findingsOpen: 'Close or cancel every open finding before closing the operation.',
    round: 'Round {{round}}',
    version: '{{code}} Rev. {{revision}} · {{title}}',
    checklistVersion: '6 common checks v{{common}} + Rev. {{revision}}',
    created: '{{name}} · {{time}}',
    createdLabel: 'Created',
    operationCard: 'Planned Operation',
    location: 'Location',
    plannedStart: 'Planned Start',
    plannedEnd: 'Planned End',
    residual: 'Target Residual Risk',
    acceptance: 'Acceptance',
    checklist: 'Checklist',
    checklistRound: 'Checklist · Round {{round}}',
    progress: '{{answered}} of {{total}} answered',
    history: 'History',
    briefing: 'Crew Briefing',
    briefingNote:
      'Each crew member taking part confirms that they were briefed on this risk assessment and the controls (SS §4).',
    noBriefing: 'No briefing acknowledged yet.',
    briefedBy: '{{name}} · {{time}}',
    notAuthorization:
      'Verification completed. This is not permission to start: the master or an authorised officer must authorise the operation.',
    willHold: 'Submitting will put the operation On Hold: at least one check is answered No.',
    willEscalate: 'Submitting will escalate the operation: a new uncontrolled hazard was reported.',
    masterRequired:
      'High residual risk (accepted by the DPA at publication): only the master can authorise, accepting the residual risk on board.',
    critical: 'Critical residual risk: the work cannot be authorised.',
    returned: 'Returned for correction: change the answers or the operation and submit again.',
    expired: 'The authorisation has expired. Start a reverification before the operation begins.',
    authorized: 'Authorised by {{name}} until {{time}}.',
    readOnly: 'Read only',
    save: 'Save Answers',
    unsaved: '{{count}} unsaved answers',
    invalid: 'Give a reason for every No or N/A answer.',
    sameActor: 'Verified and authorised by the same person',
    masterAcceptance: 'Residual risk accepted by the master',
    outcome: 'Result: {{status}}',
    residualBand: 'Residual risk at decision: {{band}}',
    reauthenticated: 'Password confirmed',
  },
  groups: {
    common: 'Common Checks',
    activity: 'Activity-Specific Checks',
    control: 'Mandatory Controls of the Assessment',
  },
  common: {
    valid_ra: 'Valid risk assessment available',
    crew_briefed: 'Crew briefed and competent',
    equipment_ready: 'Equipment inspected and operational',
    conditions_assessed: 'Weather and operational conditions assessed',
    controls_implemented: 'Required control measures implemented',
    no_uncontrolled_hazards: 'No additional uncontrolled hazards identified',
  },
  hints: {
    valid_ra: 'The current version shown above applies to this operation.',
    crew_briefed: 'Link to the briefing below.',
    equipment_ready: 'Note the inspection or test as evidence.',
    conditions_assessed: 'Confirm the current weather and conditions.',
    controls_implemented: 'Every mandatory control below is in place.',
    no_uncontrolled_hazards: 'A “No” escalates the operation.',
  },
  answers: {
    label: 'Answer for {{item}}',
    yes: 'Yes',
    no: 'No',
    na: 'N/A',
    comment: 'Reason / Comment',
    commentRequired: 'Reason (required for No and N/A)',
    evidence: 'Evidence Note',
    history: 'Earlier answers ({{count}})',
    historyItem: 'Round {{round}} · {{answer}} · {{name}} · {{time}}',
  },
  actions: {
    edit: 'Edit Operation',
    submit: 'Submit for Authorization',
    authorize: 'Authorize',
    return: 'Return',
    reject: 'Reject',
    start: 'Start Operation',
    conditions: 'Conditions Changed',
    stop: 'Stop Work',
    reverify: 'Start Reverification',
    complete: 'Complete Operation',
    close: 'Close',
    cancel: 'Cancel Verification',
    brief: 'Acknowledge Briefing',
  },
  dialogs: {
    submit: {
      title: 'Submit {{number}}',
      body: 'The answers are locked and the verification goes to the master or an authorised officer.',
    },
    authorize: {
      title: 'Authorize {{number}}',
      body: 'You authorise this operation under the conditions recorded in the verification. Your name, role, the time and the RA and checklist versions are recorded.',
      validUntil: 'Valid Until (Ship Time)',
      validityNote: 'At most 24 hours. After that a reverification is needed.',
      masterNote: 'You also accept the High residual risk on board as master.',
      sameActorNote: 'You verified this operation yourself; this will be recorded.',
    },
    return: {
      title: 'Return {{number}}',
      body: 'The verification goes back for correction. Give the reason.',
    },
    reject: {
      title: 'Reject {{number}}',
      body: 'The operation will not be carried out under this verification. Give the reason.',
    },
    start: { title: 'Start {{number}}', body: 'Record that the operation has started now.' },
    conditions: {
      title: 'Conditions Changed — {{number}}',
      body: 'A material change (weather, crew, equipment, location or method) invalidates the authorisation; a reverification is required.',
    },
    stop: {
      title: 'Stop Work — {{number}}',
      body: 'Stop the operation now. The stop is recorded with your name and the reason; reverify before continuing.',
    },
    reverify: {
      title: 'Reverification — {{number}}',
      body: 'A new round of checks starts. Earlier answers stay in the history.',
    },
    complete: {
      title: 'Complete {{number}}',
      body: 'Record the completion details of the operation.',
    },
    close: {
      title: 'Close {{number}}',
      body: 'Close the completed operation. It becomes read only.',
    },
    cancel: {
      title: 'Cancel {{number}}',
      body: 'The verification is cancelled and kept in the history.',
    },
    brief: {
      title: 'Briefing — {{number}}',
      body: 'I confirm that I was briefed on this risk assessment, its hazards and controls for this operation.',
    },
  },
  notice: {
    created: 'Verification {{number}} created.',
    saved: 'Answers saved.',
    event: '{{number}}: {{event}}.',
  },
  errors: {
    GENERIC: 'The action failed',
    CHECKS_INCOMPLETE: 'Answer every check before submitting.',
    NA_NOT_ALLOWED: 'N/A is not allowed for this check.',
    COMMENT_REQUIRED: 'Give a reason for every No or N/A answer.',
    VERIFICATION_LOCKED:
      'The verification is locked: answers can only change after a return or reverification.',
    AUTHORIZATION_EXPIRED: 'The authorisation has expired: start a reverification.',
    MASTER_ACCEPTANCE_REQUIRED: 'High residual risk: only the master can authorise.',
    RESIDUAL_UNACCEPTABLE: 'Critical residual risk: the work cannot be authorised.',
    VALIDITY_OUT_OF_RANGE: 'The validity must be in the future and at most 24 hours.',
    RA_VERSION_SUPERSEDED:
      'A newer version of this risk assessment has been published: start a new verification with it.',
    RA_VERSION_NOT_ASSIGNED: 'This risk assessment is not assigned to the vessel.',
    CONTENT_HASH_MISMATCH: 'The verification changed in the meantime. Reload and check again.',
    ALREADY_BRIEFED: 'You have already acknowledged the briefing.',
    INVALID_TRANSITION: 'This action is not possible in the current status.',
    FORBIDDEN: 'You are not authorised for this action. The attempt has been recorded.',
    REASON_REQUIRED: 'Give a reason (at least 5 characters).',
    INVALID_RA_VERIFICATION: 'Enter the planned operation; the end must be after the start.',
    NOT_ASSIGNED: 'Your role on this vessel could not be confirmed.',
    REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
    FINDINGS_BLOCKING: 'An open blocking finding holds the authorisation.',
    FINDINGS_OPEN: 'The operation cannot be closed while a finding is open.',
  },
};

export const riskVerificationTr: typeof riskVerificationEn = {
  title: 'Operasyonel Doğrulama',
  description:
    'Planlanan operasyonu iş başlamadan güncel şirket risk değerlendirmesine göre doğrulayın: altı ortak madde, faaliyete özel maddeler ve zorunlu kontroller; sonra kaptanın ya da yetkili zabitin yetkisi.',
  shipNote:
    'Altı “Evet” işe başlama izni değildir: doğrulama Yetki Bekliyor olur. Karşılanmayan madde işi bekletir; kontrolsüz yeni tehlike yükseltilir. Gemideki herkes işi her an durdurabilir.',
  officeNote: 'Doğrulamalar gemide yapılır ve senkronla gelir; ofis yalnız görür.',
  vessel: 'Gemi',
  stats: {
    open: 'Doğrulanıyor',
    pending: 'Yetki Bekliyor',
    blocked: 'Bekletilen / Durdurulan',
    active: 'Yetkili / Sürüyor',
  },
  search: 'Numara, operasyon, yer ya da risk değerlendirmesiyle arayın',
  filter: {
    state: 'Durum',
    states: {
      all: 'Tümü',
      open: 'Doğrulanıyor',
      pending_authorization: 'Yetki bekliyor',
      blocked: 'Bekletilen, yükseltilen, durdurulan ya da yeniden doğrulanacak',
      active: 'Yetkili ya da sürüyor',
      closed: 'Tamamlanan, kapanan ya da iptal',
    },
  },
  new: 'Yeni Doğrulama',
  columns: {
    number: 'No.',
    operation: 'Planlanan Operasyon',
    assessment: 'Risk Değerlendirmesi',
    planned: 'Plan (Gemi Saati)',
    checks: 'Maddeler',
    residual: 'Kalan Risk',
    status: 'Durum',
    authorization: 'Yetki',
  },
  open: '{{number}} doğrulamasını aç',
  none: '—',
  checksValue: '{{answered}} / {{total}}',
  failedCount: '{{count}} Hayır',
  validUntil: '{{time}} saatine dek geçerli',
  until: 'bitiş {{time}}',
  showing: '{{total}} kayıttan {{from}}–{{to}}',
  pagination: 'Doğrulama sayfaları',
  tableLabel: 'Operasyonel doğrulamalar',
  empty: {
    title: 'Henüz doğrulama yok',
    ship: 'Palamar, demirleme, yakıt ikmali ya da kapalı alana giriş gibi bir operasyondan önce doğrulama başlatın.',
    office: 'Bu gemi henüz doğrulama göndermedi.',
    noMatch: 'Süzgeçlere uyan doğrulama yok.',
    error: 'Doğrulamalar yüklenemedi',
    noVessel: 'Görebildiğiniz gemi yok.',
    noCandidates: 'Bu gemiye atanmış şirket risk değerlendirmesi henüz yok. Ofis yayımlar ve atar.',
  },
  statuses: {
    draft: 'Taslak',
    incomplete: 'Eksik',
    verification_completed: 'Doğrulama Tamam',
    pending_authorization: 'Yetki Bekliyor',
    on_hold: 'Bekletildi',
    escalated: 'Yükseltildi',
    authorized: 'Yetki Verildi',
    in_progress: 'Sürüyor',
    suspended: 'Durduruldu',
    completed: 'Tamamlandı',
    closed: 'Kapandı',
    cancelled: 'İptal',
    reverification_required: 'Yeniden Doğrulama Gerekli',
  },
  events: {
    submitted: 'Gönderildi',
    returned: 'Düzeltmeye iade edildi',
    rejected: 'Reddedildi',
    authorized: 'Yetki verildi',
    started: 'Operasyon başladı',
    conditions_changed: 'Koşullar değişti',
    stopped: 'İş durduruldu',
    reverification_started: 'Yeniden doğrulama başladı',
    completed: 'Operasyon tamamlandı',
    closed: 'Kapandı',
    cancelled: 'İptal edildi',
    briefed: 'Brifing onaylandı',
  },
  create: {
    title: 'Yeni Operasyonel Doğrulama',
    version: 'Risk Değerlendirmesi (güncel sürüm)',
    versionPlaceholder: 'Bu operasyonun değerlendirmesini seçin',
    blocked: 'Critical kalan risk: işe yetki verilemez.',
    masterRequired: 'High kalan risk: yetkiyi yalnız kaptan verir.',
    items: '{{count}} madde: 6 ortak, {{activity}} faaliyete özel, {{controls}} zorunlu kontrol',
    operation: 'Planlanan Operasyon',
    operationPlaceholder: 'ör. Aliağa’ya iskele tarafı yanaşma',
    location: 'Yer',
    plannedStart: 'Planlanan Başlangıç (Gemi Saati)',
    plannedEnd: 'Planlanan Bitiş (Gemi Saati)',
    timesNote: 'Saatler gemi saatiyle ({{offset}}) girilir, UTC saklanır.',
    confirm: 'Doğrulamayı Aç',
  },
  edit: {
    title: 'Planlanan Operasyonu Düzelt',
    confirm: 'Kaydet',
  },
  detail: {
    back: 'Bütün doğrulamalar',
    findingsCard: 'Bulgular',
    noFindings: 'Bu doğrulamada bulgu yok.',
    reportFinding: 'Bulgu Bildir',
    findingsBlocking:
      'Açık engelleyici bulgu yetkiyi durduruyor: önce düzeltip yeniden doğrulayın ve kapatın.',
    findingsOpen: 'Operasyonu kapatmadan önce açık her bulguyu kapatın ya da iptal edin.',
    round: '{{round}}. tur',
    version: '{{code}} Rev. {{revision}} · {{title}}',
    checklistVersion: '6 ortak madde s{{common}} + Rev. {{revision}}',
    created: '{{name}} · {{time}}',
    createdLabel: 'Açılış',
    operationCard: 'Planlanan Operasyon',
    location: 'Yer',
    plannedStart: 'Planlanan Başlangıç',
    plannedEnd: 'Planlanan Bitiş',
    residual: 'Hedef Kalan Risk',
    acceptance: 'Kabul',
    checklist: 'Kontrol Listesi',
    checklistRound: 'Kontrol Listesi · {{round}}. tur',
    progress: '{{total}} maddeden {{answered}} cevaplandı',
    history: 'Geçmiş',
    briefing: 'Mürettebat Brifingi',
    briefingNote:
      'Operasyona katılan her kişi bu risk değerlendirmesi ve kontrolleri hakkında brifing aldığını onaylar (SS §4).',
    noBriefing: 'Henüz brifing onayı yok.',
    briefedBy: '{{name}} · {{time}}',
    notAuthorization:
      'Doğrulama tamamlandı. Bu işe başlama izni değildir: operasyona kaptan ya da yetkili zabit yetki vermelidir.',
    willHold: 'Gönderince operasyon Bekletilir: en az bir maddeye Hayır cevabı verildi.',
    willEscalate: 'Gönderince operasyon yükseltilir: kontrolsüz yeni bir tehlike bildirildi.',
    masterRequired:
      'High kalan risk (DPA yayımda kabul etti): yetkiyi yalnız kaptan, kalan riski gemide kabul ederek verir.',
    critical: 'Critical kalan risk: işe yetki verilemez.',
    returned: 'Düzeltmeye iade edildi: cevapları ya da operasyonu düzeltip yeniden gönderin.',
    expired: 'Yetkinin süresi doldu. Operasyon başlamadan yeniden doğrulama başlatın.',
    authorized: '{{name}} {{time}} saatine dek yetki verdi.',
    readOnly: 'Salt okunur',
    save: 'Cevapları Kaydet',
    unsaved: '{{count}} kaydedilmemiş cevap',
    invalid: 'Her Hayır ya da N/A cevabına gerekçe yazın.',
    sameActor: 'Doğrulayan ve yetki veren aynı kişi',
    masterAcceptance: 'Kalan riski kaptan kabul etti',
    outcome: 'Sonuç: {{status}}',
    residualBand: 'Karar anındaki kalan risk: {{band}}',
    reauthenticated: 'Parola doğrulandı',
  },
  groups: {
    common: 'Ortak Maddeler',
    activity: 'Faaliyete Özel Maddeler',
    control: 'Değerlendirmenin Zorunlu Kontrolleri',
  },
  common: {
    valid_ra: 'Geçerli risk değerlendirmesi var',
    crew_briefed: 'Mürettebat brifing aldı ve yetkin',
    equipment_ready: 'Ekipman kontrol edildi ve çalışıyor',
    conditions_assessed: 'Hava ve operasyon koşulları değerlendirildi',
    controls_implemented: 'Gerekli kontrol önlemleri uygulandı',
    no_uncontrolled_hazards: 'Kontrolsüz ek tehlike tespit edilmedi',
  },
  hints: {
    valid_ra: 'Yukarıdaki güncel sürüm bu operasyona uygundur.',
    crew_briefed: 'Aşağıdaki brifinge bağlayın.',
    equipment_ready: 'Kontrolü ya da testi kanıt olarak not edin.',
    conditions_assessed: 'Güncel hava ve koşulları teyit edin.',
    controls_implemented: 'Aşağıdaki her zorunlu kontrol yerinde.',
    no_uncontrolled_hazards: '“Hayır” operasyonu yükseltir.',
  },
  answers: {
    label: '{{item}} cevabı',
    yes: 'Evet',
    no: 'Hayır',
    na: 'N/A',
    comment: 'Gerekçe / Not',
    commentRequired: 'Gerekçe (Hayır ve N/A için zorunlu)',
    evidence: 'Kanıt Notu',
    history: 'Önceki cevaplar ({{count}})',
    historyItem: '{{round}}. tur · {{answer}} · {{name}} · {{time}}',
  },
  actions: {
    edit: 'Operasyonu Düzelt',
    submit: 'Yetkiye Gönder',
    authorize: 'Yetki Ver',
    return: 'İade Et',
    reject: 'Reddet',
    start: 'Operasyonu Başlat',
    conditions: 'Koşullar Değişti',
    stop: 'İşi Durdur',
    reverify: 'Yeniden Doğrulama Başlat',
    complete: 'Operasyonu Tamamla',
    close: 'Kapat',
    cancel: 'Doğrulamayı İptal Et',
    brief: 'Brifingi Onayla',
  },
  dialogs: {
    submit: {
      title: '{{number}} gönder',
      body: 'Cevaplar kilitlenir ve doğrulama kaptana ya da yetkili zabite gider.',
    },
    authorize: {
      title: '{{number}} için yetki ver',
      body: 'Bu operasyona doğrulamada kayıtlı koşullarla yetki veriyorsunuz. Adınız, rolünüz, saat ve RA ile kontrol listesi sürümleri kaydedilir.',
      validUntil: 'Geçerlilik Sonu (Gemi Saati)',
      validityNote: 'En çok 24 saat. Sonrasında yeniden doğrulama gerekir.',
      masterNote: 'Kaptan olarak High kalan riski gemide de kabul edersiniz.',
      sameActorNote: 'Bu operasyonu kendiniz doğruladınız; bu kaydedilir.',
    },
    return: {
      title: '{{number}} iade et',
      body: 'Doğrulama düzeltmeye döner. Gerekçeyi yazın.',
    },
    reject: {
      title: '{{number}} reddet',
      body: 'Operasyon bu doğrulamayla yapılmayacak. Gerekçeyi yazın.',
    },
    start: { title: '{{number}} başlat', body: 'Operasyonun şimdi başladığını kaydedin.' },
    conditions: {
      title: 'Koşullar Değişti — {{number}}',
      body: 'Önemli bir değişiklik (hava, mürettebat, ekipman, yer ya da yöntem) yetkiyi düşürür; yeniden doğrulama gerekir.',
    },
    stop: {
      title: 'İşi Durdur — {{number}}',
      body: 'Operasyonu şimdi durdurun. Durdurma adınız ve gerekçeyle kaydedilir; devam etmeden önce yeniden doğrulayın.',
    },
    reverify: {
      title: 'Yeniden Doğrulama — {{number}}',
      body: 'Yeni bir kontrol turu başlar. Önceki cevaplar geçmişte kalır.',
    },
    complete: {
      title: '{{number}} tamamla',
      body: 'Operasyonun tamamlanma bilgisini kaydedin.',
    },
    close: { title: '{{number}} kapat', body: 'Tamamlanan operasyonu kapatın. Salt okunur olur.' },
    cancel: {
      title: '{{number}} iptal et',
      body: 'Doğrulama iptal edilir ve geçmişte kalır.',
    },
    brief: {
      title: 'Brifing — {{number}}',
      body: 'Bu operasyon için bu risk değerlendirmesi, tehlikeleri ve kontrolleri hakkında brifing aldığımı onaylıyorum.',
    },
  },
  notice: {
    created: '{{number}} doğrulaması açıldı.',
    saved: 'Cevaplar kaydedildi.',
    event: '{{number}}: {{event}}.',
  },
  errors: {
    GENERIC: 'İşlem yapılamadı',
    CHECKS_INCOMPLETE: 'Göndermeden önce her maddeyi cevaplayın.',
    NA_NOT_ALLOWED: 'Bu maddede N/A verilemez.',
    COMMENT_REQUIRED: 'Her Hayır ya da N/A cevabına gerekçe yazın.',
    VERIFICATION_LOCKED:
      'Doğrulama kilitli: cevaplar yalnız iade ya da yeniden doğrulamadan sonra değişir.',
    AUTHORIZATION_EXPIRED: 'Yetkinin süresi doldu: yeniden doğrulama başlatın.',
    MASTER_ACCEPTANCE_REQUIRED: 'High kalan risk: yetkiyi yalnız kaptan verir.',
    RESIDUAL_UNACCEPTABLE: 'Critical kalan risk: işe yetki verilemez.',
    VALIDITY_OUT_OF_RANGE: 'Geçerlilik ileri bir an ve en çok 24 saat olmalı.',
    RA_VERSION_SUPERSEDED:
      'Bu risk değerlendirmesinin yeni sürümü yayımlandı: onunla yeni doğrulama başlatın.',
    RA_VERSION_NOT_ASSIGNED: 'Bu risk değerlendirmesi gemiye atanmamış.',
    CONTENT_HASH_MISMATCH: 'Doğrulama bu arada değişti. Yenileyip yeniden bakın.',
    ALREADY_BRIEFED: 'Brifingi zaten onayladınız.',
    INVALID_TRANSITION: 'Bu işlem bugünkü durumda yapılamaz.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok. Deneme kaydedildi.',
    REASON_REQUIRED: 'Gerekçe yazın (en az 5 karakter).',
    INVALID_RA_VERIFICATION: 'Planlanan operasyonu yazın; bitiş başlangıçtan sonra olmalı.',
    NOT_ASSIGNED: 'Bu gemideki rolünüz doğrulanamadı.',
    REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
    FINDINGS_BLOCKING: 'Açık engelleyici bulgu yetkiyi durduruyor.',
    FINDINGS_OPEN: 'Bulgu açıkken operasyon kapanmaz.',
  },
};
